import { Box } from 'src/design/atoms/Box';
import { Card } from 'src/design/atoms/Card';
import { Grid } from 'src/design/atoms/Grid';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Chip } from 'src/design/atoms/Chip';
import { Input } from 'src/design/atoms/Input';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Select } from 'src/design/atoms/Select';
import { Tabs } from 'src/design/molecules/Tabs';
import { TextInput } from 'src/design/atoms/TextInput';
import { Textarea } from 'src/design/atoms/Textarea';
import { TimeInput } from 'src/design/atoms/TimeInput';
import { useFormik } from 'formik';
import { nanoid } from 'nanoid';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { DivisionDto, OrganizationOfficeDto } from 'src/api/FiretrolApiClient';
import { useWorkflowOptionsByOffice } from 'src/api/queries/workflows/useWorkflowByOffice';
import {
  useCreateOffice,
  useUpdateOffice,
} from 'src/api/queries/organization/useOfficeMutations';
import { IOrganizationOfficeQuestion } from 'src/api/models/Organization';
import { WorkflowSelectorById } from 'src/app/selectors/WorkflowSelector';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { SaveButton } from 'src/design/atoms/SaveButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import {
  applyValidationErrors,
  getErrorMessage,
  logger,
  showErrorToast,
} from 'src/utils';
import { DivisionSelector } from './DivisionSelector';
import { ModuleSelector } from './ModuleSelector';

// Curated shortlist pinned above the full America/* list from Intl.supportedValuesOf,
// covering the continental US + AK/HI so common offices don't have to scroll/search.
const TIMEZONE_SHORTLIST = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

const getTimezoneOptions = (): string[] => {
  const supported =
    typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [];
  const americas = supported.filter(
    (tz) => tz.startsWith('America/') && !TIMEZONE_SHORTLIST.includes(tz),
  );
  return [...TIMEZONE_SHORTLIST, ...americas.sort()];
};

// ISO 8601 day-of-week numbering: 1 = Monday .. 7 = Sunday.
const BUSINESS_DAY_OPTIONS = [
  { value: '1', label: 'Mon' },
  { value: '2', label: 'Tue' },
  { value: '3', label: 'Wed' },
  { value: '4', label: 'Thu' },
  { value: '5', label: 'Fri' },
  { value: '6', label: 'Sat' },
  { value: '7', label: 'Sun' },
];

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(1, 'Name is required').label('Name'),
  perDiemEnabled: Yup.boolean().nullable(),
  perDiemAmount: Yup.number()
    .nullable()
    .when('perDiemEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required('Per Diem Amount is required')
          .moreThan(0, 'Per Diem Amount must be greater than 0'),
    })
    .label('Per Diem Amount'),
  timezone: Yup.string().nullable(),
  businessHoursStart: Yup.string()
    .nullable()
    .test(
      'required-with-end',
      'Business hours start is required when business hours end is set',
      function requiredWithEnd(value) {
        return !!value || !this.parent.businessHoursEnd;
      },
    ),
  businessHoursEnd: Yup.string()
    .nullable()
    .test(
      'required-with-start',
      'Business hours end is required when business hours start is set',
      function requiredWithStart(value) {
        return !!value || !this.parent.businessHoursStart;
      },
    )
    .test(
      'after-start',
      'Business hours end must be after business hours start',
      function afterStart(value) {
        if (!value || !this.parent.businessHoursStart) return true;
        return value > this.parent.businessHoursStart;
      },
    ),
  businessDays: Yup.array()
    .of(
      Yup.number()
        .integer()
        .min(1, 'Business days must be between 1 and 7')
        .max(7, 'Business days must be between 1 and 7'),
    )
    .nullable(),
});

const defaultValues = {
  name: '',
  description: '',
  targetDivisionIds: [] as number[],
  modules: [] as string[],
  rfiWorkflowId: null,
  submittalWorkflowId: null,
  drawingWorkflowId: null,
  transmittalWorkflowId: null,
  timesheetWorkflowId: null,
  changeOrderWorkflowId: null,
  dailyModules: {
    tickets: true,
    timesheets: true,
    notes: true,
    progress: true,
    equipment: true,
    contractors: true,
    concerns: true,
    media: true,
    questions: true,
  },
  dailyQuestions: [] as IOrganizationOfficeQuestion[],
  perDiemEnabled: false,
  perDiemAmount: undefined as number | undefined,
  timezone: null as string | null,
  businessHoursStart: null as string | null,
  businessHoursEnd: null as string | null,
  businessDays: [1, 2, 3, 4, 5] as number[],
};

interface OfficeFormProps {
  initialValues?: OrganizationOfficeDto;
}

export const OfficeForm: React.FC<OfficeFormProps> = ({ initialValues }) => {
  const navigate = useNavigate();
  const createOffice = useCreateOffice();
  const updateOffice = useUpdateOffice();
  const { data: workflowOptions } = useWorkflowOptionsByOffice(
    initialValues?.id,
    'all',
  );
  const workflows = workflowOptions ?? [];
  const [questionValue, setQuestionValue] = React.useState('');
  const isEditing = !!initialValues?.id;

  const formik = useFormik({
    initialValues: initialValues
      ? {
          ...initialValues,
          targetDivisionIds:
            initialValues.targetDivisionIds ??
            initialValues.divisions?.map((d) => d.id) ??
            [],
        }
      : defaultValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      setSubmitting(true);
      try {
        if (isEditing && initialValues) {
          await updateOffice.mutateAsync({
            id: initialValues.id,
            request: values,
          });
        } else {
          const result = await createOffice.mutateAsync(values);
          navigate(`/settings/offices/edit/${result.id}`);
        }
      } catch (err: unknown) {
        logger.error('Failed to save office', { err });
        const { applied, unmatchedMessages } = applyValidationErrors(
          err,
          Object.keys(values),
          setFieldError,
        );
        if (applied && unmatchedMessages.length === 0) {
          showErrorToast('Please fix the highlighted fields below.');
        } else {
          showErrorToast(`Failed to save office: ${getErrorMessage(err)}`);
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, isSubmitting, isValid, dirty, setFieldValue } = formik;

  const timezoneOptions = React.useMemo(() => getTimezoneOptions(), []);

  const onDivisionChange = (divisions: DivisionDto[]) => {
    setFieldValue(
      'targetDivisionIds',
      divisions.map((d) => d.id),
    );
  };

  const addQuestion = () => {
    const newQuestion = {
      id: nanoid(),
      question: questionValue,
    };
    setFieldValue('dailyQuestions', [...values.dailyQuestions, newQuestion]);
    setQuestionValue('');
  };

  const removeQuestion = (question: IOrganizationOfficeQuestion) => {
    setFieldValue(
      'dailyQuestions',
      values.dailyQuestions.filter((q) => q.id !== question.id),
    );
  };

  const updateDailyModules = (field: string, value: boolean) => {
    setFieldValue('dailyModules', { ...values.dailyModules, [field]: value });
  };

  const renderQuestions = () => {
    if (values.dailyQuestions.length === 0) {
      return <Text>No questions to display</Text>;
    }
    return values.dailyQuestions.map((q) => (
      <Group key={q.id} justify="space-between" mb="xs">
        <Text>{q.question}</Text>
        <DeleteActionIcon
          requiredConfirmation={false}
          onDelete={() => removeQuestion(q)}
        />
      </Group>
    ));
  };

  return (
    <>
      <Group justify="flex-end" mb="md">
        <SecondaryButton onClick={() => navigate('/settings/offices')}>
          Cancel
        </SecondaryButton>
      </Group>
      <Card>
        <Tabs defaultValue="details" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="details">Office Details</Tabs.Tab>
            <Tabs.Tab value="modules">Modules</Tabs.Tab>
            <Tabs.Tab value="workflows">Workflows</Tabs.Tab>
            <Tabs.Tab value="dailys">Dailys</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details">
            <Grid mt="md" maw={800}>
              <Grid.Col span={6}>
                <Stack>
                  <TextInput
                    label="Office Name"
                    placeholder="What do you want to call this office?"
                    withAsterisk
                    value={values.name}
                    onChange={(e) => setFieldValue('name', e.target.value)}
                    error={formik.touched.name && formik.errors.name}
                  />

                  <Textarea
                    label="Description"
                    placeholder="Enter a description for this office..."
                    value={values.description}
                    onChange={(e) =>
                      setFieldValue('description', e.target.value)
                    }
                    autosize
                    minRows={3}
                  />

                  <Checkbox
                    label="Default Service Job Per Diem"
                    description="Applied as the default only when a new service job is created for this office. Each job's per diem is managed on the job."
                    checked={values.perDiemEnabled ?? false}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setFieldValue('perDiemEnabled', enabled);
                      if (!enabled) {
                        setFieldValue('perDiemAmount', undefined);
                      }
                    }}
                  />

                  {values.perDiemEnabled && (
                    <NumberInput
                      label="Per Diem Amount"
                      placeholder="0.00"
                      withAsterisk
                      prefix="$"
                      min={0}
                      decimalScale={2}
                      value={values.perDiemAmount ?? undefined}
                      onChange={(value) =>
                        setFieldValue(
                          'perDiemAmount',
                          typeof value === 'number' ? value : undefined,
                        )
                      }
                      error={
                        formik.touched.perDiemAmount &&
                        formik.errors.perDiemAmount
                      }
                    />
                  )}
                </Stack>
              </Grid.Col>
              <Grid.Col span={6}>
                <Input.Wrapper
                  label="CTC Divisions"
                  description="Divisions control view access to CTC and related jobs. A Division may only be assigned to one office."
                >
                  <Box mt="md">
                    <DivisionSelector
                      initialValue={values.targetDivisionIds}
                      onChange={onDivisionChange}
                      filter={(division) =>
                        division.offices?.length === 0 ||
                        (values.targetDivisionIds ?? []).includes(division.id)
                      }
                    />
                  </Box>
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={12}>
                <Title order={5} mb="md">
                  SMS Notification Schedule
                </Title>
                <Stack maw={400}>
                  <Select
                    label="Timezone"
                    placeholder="Select a timezone"
                    description="Determines when business hours apply for this office."
                    searchable
                    clearable
                    data={timezoneOptions}
                    value={values.timezone}
                    onChange={(value) => setFieldValue('timezone', value)}
                  />

                  <Group grow>
                    <TimeInput
                      label="Business Hours Start"
                      value={values.businessHoursStart ?? ''}
                      onChange={(e) =>
                        setFieldValue(
                          'businessHoursStart',
                          e.currentTarget.value || null,
                        )
                      }
                      onBlur={() => {
                        // Both fields are validated together (both-or-neither, end
                        // after start), so blurring either should reveal any error
                        // that involves the other field too.
                        formik.setFieldTouched('businessHoursStart', true);
                        formik.setFieldTouched('businessHoursEnd', true);
                      }}
                      error={
                        formik.touched.businessHoursStart &&
                        formik.errors.businessHoursStart
                      }
                    />
                    <TimeInput
                      label="Business Hours End"
                      value={values.businessHoursEnd ?? ''}
                      onChange={(e) =>
                        setFieldValue(
                          'businessHoursEnd',
                          e.currentTarget.value || null,
                        )
                      }
                      onBlur={() => {
                        formik.setFieldTouched('businessHoursStart', true);
                        formik.setFieldTouched('businessHoursEnd', true);
                      }}
                      error={
                        formik.touched.businessHoursEnd &&
                        formik.errors.businessHoursEnd
                      }
                    />
                  </Group>

                  <Input.Wrapper label="Business Days">
                    <Chip.Group
                      multiple
                      value={(values.businessDays ?? []).map(String)}
                      onChange={(value) =>
                        setFieldValue(
                          'businessDays',
                          value.map((v) => Number(v)).sort(),
                        )
                      }
                    >
                      <Group mt="xs">
                        {BUSINESS_DAY_OPTIONS.map((day) => (
                          <Chip key={day.value} value={day.value}>
                            {day.label}
                          </Chip>
                        ))}
                      </Group>
                    </Chip.Group>
                  </Input.Wrapper>
                </Stack>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="modules">
            <Box mt="md">
              <ModuleSelector
                initialSelected={values.modules}
                onSelectedChange={(value) => setFieldValue('modules', value)}
              />
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="workflows">
            <Grid mt="md">
              <Grid.Col span={4}>
                <Input.Wrapper label="RFI Workflow">
                  <WorkflowSelectorById
                    options={workflows}
                    resourceId="rfi"
                    value={values.rfiWorkflowId}
                    onChange={(value) => setFieldValue('rfiWorkflowId', value)}
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Submittal Workflow">
                  <WorkflowSelectorById
                    options={workflows}
                    resourceId="submittal"
                    value={values.submittalWorkflowId}
                    onChange={(value) =>
                      setFieldValue('submittalWorkflowId', value)
                    }
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Drawing Workflow">
                  <WorkflowSelectorById
                    options={workflows}
                    resourceId="drawing"
                    value={values.drawingWorkflowId}
                    onChange={(value) =>
                      setFieldValue('drawingWorkflowId', value)
                    }
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Transmittal Workflow">
                  <WorkflowSelectorById
                    options={workflows}
                    resourceId="transmittal"
                    value={values.transmittalWorkflowId}
                    onChange={(value) =>
                      setFieldValue('transmittalWorkflowId', value)
                    }
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Timesheet Workflow">
                  <WorkflowSelectorById
                    options={workflows}
                    resourceId="timesheet"
                    value={values.timesheetWorkflowId}
                    onChange={(value) =>
                      setFieldValue('timesheetWorkflowId', value)
                    }
                  />
                </Input.Wrapper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Input.Wrapper label="Change Order Workflow">
                  <WorkflowSelectorById
                    options={workflows}
                    resourceId="change"
                    value={values.changeOrderWorkflowId}
                    onChange={(value) =>
                      setFieldValue('changeOrderWorkflowId', value)
                    }
                  />
                </Input.Wrapper>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>

          <Tabs.Panel value="dailys">
            <Grid mt="md">
              <Grid.Col span={8}>
                <Paper maw={700}>
                  <Title order={5} mb="md">
                    Default Questions
                  </Title>
                  {renderQuestions()}
                  <Group mt="md">
                    <TextInput
                      placeholder="Enter question..."
                      value={questionValue}
                      onChange={(e) => setQuestionValue(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <CreateButton
                      onClick={addQuestion}
                      disabled={questionValue.length === 0}
                    />
                  </Group>
                </Paper>
              </Grid.Col>
              <Grid.Col span={4}>
                <Paper>
                  <Title order={5} mb="md">
                    Include Modules
                  </Title>
                  <Stack>
                    <Checkbox
                      label="Work Tickets"
                      checked={values.dailyModules.tickets}
                      onChange={(e) =>
                        updateDailyModules('tickets', e.target.checked)
                      }
                    />
                    <Checkbox
                      label="Timesheets"
                      checked={values.dailyModules.timesheets}
                      onChange={(e) =>
                        updateDailyModules('timesheets', e.target.checked)
                      }
                    />
                    <Checkbox
                      label="Progress"
                      checked={values.dailyModules.progress}
                      onChange={(e) =>
                        updateDailyModules('progress', e.target.checked)
                      }
                    />
                    <Checkbox
                      label="Media"
                      checked={values.dailyModules.media}
                      onChange={(e) =>
                        updateDailyModules('media', e.target.checked)
                      }
                    />
                    <Checkbox
                      label="Questions"
                      checked={values.dailyModules.questions}
                      onChange={(e) =>
                        updateDailyModules('questions', e.target.checked)
                      }
                    />
                  </Stack>
                </Paper>
              </Grid.Col>
            </Grid>
          </Tabs.Panel>
        </Tabs>
      </Card>

      <Group mt="md">
        {isEditing ? (
          <SaveButton
            onClick={() => formik.submitForm()}
            loading={isSubmitting}
            disabled={!isValid || !dirty}
          />
        ) : (
          <CreateButton
            onClick={() => formik.submitForm()}
            loading={isSubmitting}
            disabled={!isValid || !dirty}
          >
            Create Office
          </CreateButton>
        )}
      </Group>
    </>
  );
};
